/**
 * CollectorUp — Social handle normalization utility.
 *
 * All social profile inputs (Instagram, Facebook, TikTok, YouTube) accept ONLY
 * the collector's handle. The platform URL prefix is shown as a fixed prefix
 * visually. The normalizer:
 *
 *   • strips leading/trailing whitespace
 *   • strips a leading "@"
 *   • strips any pasted matching-platform URL prefix (so users who paste the
 *     full URL don't create a duplicated URL like
 *     https://instagram.com/https://instagram.com/gilmaakaroun)
 *   • rejects obviously invalid characters
 *
 * `platform` MUST be one of the keys of PLATFORMS below.
 *
 * Returns { ok: true, handle, fullUrl } on success,
 * or       { ok: false, error }         on failure.
 */

export const PLATFORMS = {
  instagram: {
    prefix: "https://www.instagram.com/",
    label: "instagram.com/",
    stripHosts: ["instagram.com", "www.instagram.com"],
    stripAtInHost: false,
  },
  facebook: {
    prefix: "https://www.facebook.com/",
    label: "facebook.com/",
    stripHosts: ["facebook.com", "www.facebook.com", "fb.com", "m.facebook.com"],
    stripAtInHost: false,
  },
  tiktok: {
    prefix: "https://www.tiktok.com/@",
    label: "tiktok.com/@",
    stripHosts: ["tiktok.com", "www.tiktok.com", "vm.tiktok.com"],
    stripAtInHost: true,      // TikTok URLs include a leading "@" in the path
  },
  youtube: {
    prefix: "https://www.youtube.com/@",
    label: "youtube.com/@",
    stripHosts: ["youtube.com", "www.youtube.com", "m.youtube.com", "youtu.be"],
    stripAtInHost: true,      // YouTube handle URLs use "@handle"
  },
};

// A conservative charset that covers the union of handle rules across the four
// platforms (letters, digits, underscore, dot, hyphen). We intentionally reject
// spaces, slashes, and anything URL-encoded.
const HANDLE_RE = /^[A-Za-z0-9._-]{1,60}$/;

function stripPlatformPrefix(raw, platform) {
  if (!raw) return "";
  const cfg = PLATFORMS[platform];
  let s = raw.trim();

  // Full URL paste — parse and pull out the last non-empty path segment.
  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s);
      const host = u.hostname.toLowerCase();
      if (cfg.stripHosts.includes(host)) {
        // Path is like "/handle" or "/@handle" (tiktok/youtube) — pull first segment
        let path = u.pathname.replace(/^\/+/, "").replace(/\/+$/, "");
        if (cfg.stripAtInHost && path.startsWith("@")) path = path.slice(1);
        // Take first segment only; ignore query/fragments
        s = path.split("/")[0];
      } else {
        // URL for a different platform — reject
        return "";
      }
    } catch {
      return "";
    }
  }

  // Bare paste of "@handle" or "instagram.com/handle" etc.
  s = s.replace(/^@+/, "");
  const bareHostPatterns = cfg.stripHosts.map((h) =>
    new RegExp("^" + h.replace(/\./g, "\\.") + "/@?", "i")
  );
  for (const re of bareHostPatterns) {
    s = s.replace(re, "");
  }
  return s.trim();
}

/** Normalize a user-entered social handle. */
export function normalizeHandle(platform, raw) {
  if (!PLATFORMS[platform]) return { ok: false, error: "Unknown platform" };
  const cfg = PLATFORMS[platform];
  const handle = stripPlatformPrefix(raw, platform);
  if (!handle) return { ok: false, error: "" };
  if (!HANDLE_RE.test(handle)) {
    return { ok: false, error: "Invalid characters — use letters, digits, underscore, dot, or hyphen." };
  }
  return {
    ok: true,
    handle,
    fullUrl: cfg.prefix + handle,
  };
}

/**
 * Convert any legacy full-URL value stored on a profile into a bare handle.
 * If the URL is unparseable or points at a foreign host, returns "".
 */
export function extractHandleFromLegacy(platform, storedValue) {
  if (!storedValue) return "";
  const cfg = PLATFORMS[platform];
  if (!cfg) return "";
  const { ok, handle } = normalizeHandle(platform, storedValue);
  return ok ? handle : "";
}
